import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";
import {
  ArrowRight,
  BookOpen,
  CalendarCheck,
  CheckSquare,
  ChevronRight,
  FileText,
  Headphones,
  Lightbulb,
  MessageCircle,
  Phone,
  PlayCircle,
  Scale,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
} from "lucide-react";

const STEPS = [
  {
    number: "01",
    label: "You start with a question",
    headline: "You arrive with a real need",
    body: "Search, click an ad, or receive an SMS — you land here with a genuine question already in mind.",
    example: '"How do I buy land safely?" or "How do I process a visa?"',
    icon: Search,
    color: "#FFD221",
    bgClass: "from-[#FFD221]/20 to-[#FFD221]/5",
    content: null,
  },
  {
    number: "02",
    label: "You learn",
    headline: "Clear content, zero jargon",
    body: "We serve exactly what you need to understand the topic — in the format that works best for you.",
    example: null,
    icon: BookOpen,
    color: "#1e40af",
    bgClass: "from-blue-100/60 to-blue-50/20",
    content: [
      { icon: FileText, label: "Articles" },
      { icon: BookOpen, label: "Guides" },
      { icon: Scale, label: "Facts & Reports" },
      { icon: PlayCircle, label: "Videos" },
      { icon: Headphones, label: "Podcasts" },
      { icon: SlidersHorizontal, label: "Comparisons" },
      { icon: CheckSquare, label: "Checklists" },
      { icon: Lightbulb, label: "Tips" },
    ],
  },
  {
    number: "03",
    label: "You build confidence",
    headline: "Know exactly what matters",
    body: "You understand what to do, what to avoid, and what comes next — before you speak to anyone.",
    example: null,
    icon: ShieldCheck,
    color: "#16a34a",
    bgClass: "from-green-100/60 to-green-50/20",
    content: null,
  },
  {
    number: "04",
    label: "You take action",
    headline: "Connect with a verified expert",
    body: "When you're ready, we route you to the right professional — through the channel that suits you best.",
    example: null,
    icon: CalendarCheck,
    color: "#7c3aed",
    bgClass: "from-violet-100/60 to-violet-50/20",
    content: [
      { icon: MessageCircle, label: "WhatsApp" },
      { icon: Phone, label: "Call" },
      { icon: CalendarCheck, label: "Appointment" },
      { icon: FileText, label: "Form" },
    ],
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0);
  const step = STEPS[activeStep];
  const StepIcon = step.icon;

  return (
    <section className="overflow-hidden relative px-4 py-20 bg-white sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-[#FFD221]/10 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-[#FFD221]/8 blur-3xl" aria-hidden />

      <div className="relative mx-auto max-w-8xl">
        {/* ── Section header ─────────────────────────────── */}
        <motion.div
          className="mb-12 text-center sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/12 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#9a7b00] shadow-sm">
            <Smartphone size={13} strokeWidth={2.5} aria-hidden />
            How InfoBoxx works
          </span>
          <h2 className="mt-4 text-[clamp(2rem,4vw+0.85rem,3.25rem)] font-black leading-[1.06] tracking-tight text-gray-950">
            Four steps.{" "}
            <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#ffec80] bg-clip-text text-transparent">
              Total clarity.
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[0.9375rem] leading-relaxed text-gray-500">
            InfoBoxx does not interrupt your journey —{" "}
            <span className="font-semibold text-gray-800">it guides it.</span>
          </p>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] lg:items-stretch lg:gap-14">
          {/* ── Step navigation ────────────────────────────── */}
          <motion.div
            className="flex flex-col gap-3"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = i === activeStep;
              return (
                <motion.button
                  key={s.number}
                  type="button"
                  variants={fadeUp}
                  onClick={() => setActiveStep(i)}
                  className={`group relative overflow-hidden rounded-2xl border text-left transition-all duration-300 ${
                    active
                      ? "border-black/12 bg-white shadow-[0_16px_50px_-28px_rgba(0,0,0,0.4)]"
                      : "border-black/[0.07] bg-white/60 shadow-sm hover:border-black/12 hover:bg-white hover:shadow-md"
                  }`}
                  whileTap={{ scale: 0.99 }}
                  aria-pressed={active}
                >
                  {active && (
                    <motion.div
                      layoutId="step-accent"
                      className="absolute top-0 bottom-0 left-0 w-1 rounded-r-full"
                      style={{ background: s.color }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}

                  <div className="flex gap-4 items-center px-5 py-4">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-all duration-300 ${
                        active ? "shadow-lg" : "bg-gray-100 group-hover:bg-gray-50"
                      }`}
                      style={
                        active
                          ? { background: `${s.color}22`, boxShadow: `0 8px 24px -8px ${s.color}80` }
                          : undefined
                      }
                    >
                      <Icon
                        size={20}
                        strokeWidth={2}
                        style={{ color: active ? s.color : "#9ca3af" }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[10px] font-black uppercase tracking-[0.18em]"
                        style={{ color: active ? s.color : "#9ca3af" }}
                      >
                        Step {s.number}
                      </p>
                      <p className={`truncate text-sm font-black leading-snug ${active ? "text-gray-950" : "text-gray-600"}`}>
                        {s.label}
                      </p>
                    </div>

                    <ChevronRight
                      size={16}
                      className={`shrink-0 transition-all duration-300 ${
                        active ? "text-gray-500 translate-x-0" : "text-gray-300 -translate-x-1 group-hover:translate-x-0 group-hover:text-gray-400"
                      }`}
                      strokeWidth={2.5}
                    />
                  </div>

                  {active && (
                    <motion.div
                      className="h-0.5 origin-left rounded-full"
                      style={{ background: `linear-gradient(90deg, ${step.color}, transparent)` }}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                      key={`bar-${i}`}
                    />
                  )}
                </motion.button>
              );
            })}

            {/* ── Key message ───────────────────────────────── */}
            <motion.div
              variants={fadeUp}
              className="mt-2 flex items-start gap-3 rounded-2xl border border-[#FFD221]/30 bg-gradient-to-b from-[#FFD221]/12 to-white/40 px-5 py-4 shadow-sm"
            >
              <span className="mt-0.5 text-xl leading-none" aria-hidden>💡</span>
              <p className="text-sm font-semibold leading-relaxed text-gray-700">
                InfoBoxx does not interrupt your journey —{" "}
                <span className="font-black text-gray-900">it guides it.</span>
              </p>
            </motion.div>
          </motion.div>

          {/* ── Step detail panel ──────────────────────────── */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 24, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.99 }}
                transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                className="relative h-full overflow-hidden rounded-[2rem] border border-black/[0.08] bg-white shadow-[0_28px_80px_-44px_rgba(0,0,0,0.42)]"
                style={{
                  boxShadow: `0 32px 90px -44px ${step.color}40`,
                }}
              >
                {/* background glow */}
                <div
                  className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${step.bgClass} opacity-60`}
                  aria-hidden
                />
                <div
                  className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-25 blur-3xl pointer-events-none"
                  style={{ background: step.color }}
                  aria-hidden
                />

                <div className="flex relative flex-col p-8 h-full sm:p-10">
                  {/* step badge */}
                  <div className="flex gap-3 items-center mb-6">
                    <div
                      className="flex justify-center items-center w-14 h-14 rounded-2xl shadow-lg"
                      style={{
                        background: `linear-gradient(145deg, ${step.color}33, white)`,
                        boxShadow: `0 10px 32px -10px ${step.color}80`,
                      }}
                    >
                      <StepIcon size={26} strokeWidth={2} style={{ color: step.color }} />
                    </div>
                    <div>
                      <p
                        className="text-[11px] font-black uppercase tracking-[0.2em]"
                        style={{ color: step.color }}
                      >
                        Step {step.number}
                      </p>
                      <p className="text-lg font-black leading-tight capitalize text-gray-950">
                        {step.label}
                      </p>
                    </div>
                  </div>

                  <h3 className="text-2xl font-black leading-snug text-gray-950 sm:text-3xl">
                    {step.headline}
                  </h3>
                  <p className="mt-3 max-w-md text-[0.9375rem] leading-relaxed text-gray-600">
                    {step.body}
                  </p>

                  {step.example && (
                    <div className="mt-5 flex items-start gap-2.5 rounded-2xl border border-black/8 bg-white/80 px-4 py-3.5 shadow-sm backdrop-blur-sm">
                      <span className="mt-0.5 shrink-0 text-base" aria-hidden>💬</span>
                      <p className="text-sm italic font-semibold leading-snug text-gray-700">
                        {step.example}
                      </p>
                    </div>
                  )}

                  {step.content && (
                    <motion.div
                      className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-4"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      {step.content.map(({ icon: ItemIcon, label }) => (
                        <motion.div
                          key={label}
                          variants={fadeUp}
                          className="flex flex-col items-center gap-2 rounded-2xl border border-black/[0.07] bg-white/90 px-3 py-3.5 text-center shadow-sm"
                        >
                          <div
                            className="flex justify-center items-center w-9 h-9 rounded-xl"
                            style={{ background: `${step.color}18` }}
                          >
                            <ItemIcon size={16} strokeWidth={2} style={{ color: step.color }} />
                          </div>
                          <span className="text-[11px] font-black text-gray-800">{label}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}

                  {/* step nav footer */}
                  <div className="mt-auto flex items-center justify-between border-t border-black/[0.06] pt-6">
                    <div className="flex gap-1.5">
                      {STEPS.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActiveStep(i)}
                          className="h-1.5 rounded-full transition-all duration-300"
                          style={{
                            width: i === activeStep ? "2rem" : "0.5rem",
                            background: i === activeStep ? step.color : "#e5e7eb",
                          }}
                          aria-label={`Go to step ${i + 1}`}
                        />
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setActiveStep((p) => Math.max(0, p - 1))}
                        disabled={activeStep === 0}
                        className="flex justify-center items-center w-9 h-9 text-gray-700 bg-white rounded-xl border shadow-sm transition border-black/10 hover:border-black/20 disabled:pointer-events-none disabled:opacity-30"
                        aria-label="Previous step"
                      >
                        <ChevronRight size={16} strokeWidth={2.5} className="rotate-180" />
                      </button>
                      {activeStep < STEPS.length - 1 ? (
                        <button
                          type="button"
                          onClick={() => setActiveStep((p) => Math.min(STEPS.length - 1, p + 1))}
                          className="flex h-9 items-center gap-1.5 rounded-xl px-4 text-sm font-black text-black shadow-sm transition hover:brightness-105"
                          style={{ background: step.color }}
                          aria-label="Next step"
                        >
                          Next
                          <ChevronRight size={14} strokeWidth={2.5} />
                        </button>
                      ) : (
                        <Link
                          to="/contact"
                          className="flex h-9 items-center gap-1.5 rounded-xl bg-black px-4 text-sm font-black text-white shadow-sm transition hover:bg-gray-900"
                        >
                          Get started
                          <ArrowRight size={14} strokeWidth={2.5} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
